// ============================================
// components/chatbot/Chatbot.js
// UNNATI PATH - AI Career Assistant Chatbot
// ============================================
import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { FiMessageCircle, FiX, FiSend, FiMinimize2, FiCpu } from 'react-icons/fi';
import './Chatbot.css';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const WELCOME_MSG = {
  id: 0,
  role: 'assistant',
  text: `Namaste! 🙏 Main **UNNATI PATH AI Assistant** hoon.\n\nMain aapki madad kar sakta hoon:\n• 💼 Jobs dhundhne mein\n• 📝 Resume tips\n• 🎯 Interview preparation\n• 🏢 Companies ke baare mein\n• 📈 Career guidance\n\nKya poochna chahte hain?`,
  time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
};

const Chatbot = () => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MSG]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open && !minimized) {
      scrollToBottom();
      setUnread(0);
      inputRef.current?.focus();
    }
  }, [open, minimized, messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleToggle = () => {
    setOpen(!open);
    setMinimized(false);
    setUnread(0);
  };

  const handleMinimize = () => {
    setMinimized(true);
    setOpen(false);
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = {
      id: Date.now(),
      role: 'user',
      text,
      time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API_BASE}/chatbot`, { message: text });
      const botMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        text: res.data.reply || 'Sorry, kuch galat ho gaya. Dobara try karein.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, botMsg]);
      if (!open || minimized) setUnread(prev => prev + 1);
    } catch (err) {
      const errMsg = {
        id: Date.now() + 1,
        role: 'assistant',
        text: '❌ Server se connect nahi ho paya. Kripya baad mein try karein.',
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      setMessages(prev => [...prev, errMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const renderText = (text) => {
    // Simple markdown-ish rendering
    return text.split('\n').map((line, i) => {
      const bold = line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      return <p key={i} dangerouslySetInnerHTML={{ __html: bold || '&nbsp;' }} />;
    });
  };

  const QUICK_REPLIES = [
    '💼 Jobs kaise dhundhein?',
    '📝 Resume tips chahiye',
    '🎯 Interview tips',
    '📈 Career guidance'
  ];

  return (
    <>
      {/* Chat Window */}
      {open && !minimized && (
        <div className="chatbot-window">
          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar">
                <FiCpu size={18} />
              </div>
              <div>
                <p className="chatbot-title">UNNATI PATH Assistant</p>
                <p className="chatbot-status"><span className="status-dot" />Online</p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <button onClick={handleMinimize} title="Minimize" className="chatbot-icon-btn">
                <FiMinimize2 size={16} />
              </button>
              <button onClick={handleToggle} title="Close" className="chatbot-icon-btn">
                <FiX size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-msg chatbot-msg--${msg.role}`}>
                {msg.role === 'assistant' && (
                  <div className="chatbot-msg-avatar"><FiCpu size={14} /></div>
                )}
                <div className="chatbot-msg-bubble">
                  <div className="chatbot-msg-text">{renderText(msg.text)}</div>
                  <div className="chatbot-msg-time">{msg.time}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="chatbot-msg chatbot-msg--assistant">
                <div className="chatbot-msg-avatar"><FiCpu size={14} /></div>
                <div className="chatbot-msg-bubble">
                  <div className="chatbot-typing">
                    <span /><span /><span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Replies */}
          {messages.length <= 1 && (
            <div className="chatbot-quick-replies">
              {QUICK_REPLIES.map((q, i) => (
                <button key={i} className="quick-reply-btn" onClick={() => {
                  setInput(q);
                  setTimeout(() => sendMessage(), 50);
                }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div className="chatbot-input-area">
            <textarea
              ref={inputRef}
              className="chatbot-input"
              placeholder="Apna sawaal yahan type karein..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
            />
            <button
              className={`chatbot-send-btn ${(!input.trim() || loading) ? 'disabled' : ''}`}
              onClick={sendMessage}
              disabled={!input.trim() || loading}
            >
              <FiSend size={18} />
            </button>
          </div>
          <p className="chatbot-footer-note">Powered by UNNATI PATH AI</p>
        </div>
      )}

      {/* FAB Button */}
      <button className="chatbot-fab" onClick={handleToggle} title="Chat with AI Assistant">
        {open ? <FiX size={24} /> : <FiMessageCircle size={24} />}
        {unread > 0 && !open && (
          <span className="chatbot-badge">{unread}</span>
        )}
      </button>
    </>
  );
};

export default Chatbot;
